import Vue from 'vue';

function getInitialState() {
  return {
    subjects: {},
    subject: null,
    pagination: {},
    loading: false,
    loadingUpdate: false,
    loadingGetList: false,
    saving: false,
    isUserAllowedToCreate: false,
    loadingSubject: false,
    storageKey: 'filtersOfProjectByIdObj',
  };
}

export const ProjectSubjects = {
  namespaced: true,

  state: getInitialState(),

  getters: {
    getStorageKeyValue: (state) => state.storageKey,

    getSubjects: (state) => (uuid) => {
      return state.subjects[uuid] ?? [];
    },

    getSubject:
      ({ subjects }) =>
      (projectUuid, subjectUuid) => {
        const projectSubjects = subjects[projectUuid] ?? [];

        return projectSubjects.find(({ uuid }) => uuid === subjectUuid);
      },

    areSubjectsLoaded: (state) => (uuid) => {
      return Object.keys(state.subjects).includes(uuid);
    },

    areSubjectsLoading: (state) => state.loading,

    canCreate: ({ isUserAllowedToCreate }) => isUserAllowedToCreate,

    getPaginationData:
      ({ pagination }) =>
      (uuid) =>
        pagination?.[uuid] ?? {},

    loadingSubject: ({ loadingSubject }) => loadingSubject,

    loadingGetList: ({ loadingGetList }) => loadingGetList,

    loadingUpdate: ({ loadingUpdate }) => loadingUpdate,

    subject: ({ subject }) => subject,
  },

  mutations: {
    setLoading(state, loading) {
      state.loading = loading;
    },

    setLoadingUpdate(state, loading) {
      state.loadingUpdate = loading;
    },

    setLoadingGetList(state, loading) {
      state.loadingGetList = loading;
    },

    setPagination(state, pagination) {
      state.pagination = { ...state.pagination, ...pagination };
    },

    updateSubjects(state, newSubject) {
      state.subjects = { ...state.subjects, ...newSubject };
    },

    removeSubject(state, removeSubject) {
      let uuid = Object.keys(removeSubject)[0];
      state.subjects[uuid] = state.subjects[uuid].filter((project) => project.id !== removeSubject[uuid]);
    },

    setAddPermission(state, permission) {
      state.isUserAllowedToCreate = permission;
    },

    resetProjectSubjects(state, project) {
      Vue.delete(state.subjects, project);
      Vue.delete(state.pagination, project);
    },

    setLoadingSubject(state, bool) {
      Vue.set(state, 'loadingSubject', bool);
    },

    setSubject(state, subject) {
      Vue.set(state, 'subject', subject);
    },

    resetState(state) {
      Object.assign(state, getInitialState());
    },
  },

  actions: {
    async loadSubject({ commit }, { repository, event, project, subject, eventStatus }) {
      commit('setLoadingSubject', true);
      let responseSubject = null;

      try {
        const {
          data: { data },
        } = await this._vm.$api('repositories.projects.subjects.show', {
          event_status: eventStatus,
          event_uuid: event,
          version: '1.0',
          repository,
          project,
          subject,
        });

        responseSubject = data;
      } catch (err) {
        console.error(err);
      }

      commit('setSubject', responseSubject);
      commit('setLoadingSubject', false);
    },

    async loadSubjects({ commit }, request) {
      if (!request.project) {
        return;
      }
      const { repository, project, filter, env, cancelToken, version, page, perPage, sortBy, sortDesc, ...rest } =
        request;

      let subjects = [];
      let pagination = {};

      commit('setLoading', true);
      commit('setLoadingGetList', true);
      try {
        const response = await this._vm.$api(
          'repositories.projects.subjects.index',
          {
            version: '1.0',
            repository,
            v: version,
            sortDesc,
            perPage,
            project,
            filter,
            sortBy,
            page,
            env,
            ...rest,
          },
          {},
          cancelToken
        );

        subjects = response.data.data;
        pagination = { ...response.data.meta, ...response.data.links };

        commit('setAddPermission', response.data.can?.add);
      } catch (err) {
        console.error(err);
      }

      commit('updateSubjects', { [project]: subjects });
      commit('setPagination', { [project]: pagination });

      commit('setLoading', false);
      commit('setLoadingGetList', false);
    },

    async addSubject({ commit }, { repository, project, body }) {
      commit('setLoading', true);

      try {
        await this._vm.$api(
          'repositories.projects.subjects.store',
          {
            version: '1.0',
            repository,
            project,
          },
          body
        );
      } catch (err) {
        console.error(err);
      }

      commit('setLoading', false);
    },

    async updateSubject({ commit }, request) {
      const requestBody = request.body;
      const { repository, project, projectSubject } = request;
      commit('setLoadingUpdate', true);
      commit('setLoading', true);
      let updatedSubject = null;

      try {
        const response = await this._vm.$api(
          'repositories.projects.subjects.update',
          {
            subject: projectSubject,
            version: '1.0',
            repository,
            project,
          },
          requestBody
        );

        updatedSubject = response.data.data;
        if (updatedSubject) {
          commit('removeSubject', { [project]: projectSubject });
        }
      } catch (err) {
        console.error(err);
      }

      commit('setLoading', false);
      commit('setLoadingUpdate', false);
    },

    async deleteSubject({ commit }, request) {
      const { repository, project, projectSubject } = request;
      commit('setLoading', true);

      try {
        await this._vm.$api('repositories.projects.subjects.destroy', {
          subject: projectSubject,
          version: '1.0',
          repository,
          project,
        });

        await commit('removeSubject', { [project]: projectSubject });
      } catch (err) {
        console.error(err);
      }
      commit('setLoading', false);
    },

    resetProjectSubjectsState({ commit }) {
      commit('resetState');
    },

    updateFiltersByProject({ getters }, payload) {
      const storageKey = getters.getStorageKeyValue;
      const filteredObjects = JSON.parse(localStorage.getItem(storageKey)) || {};
      const projectId = Object.keys(payload)[0];

      if (!filteredObjects[projectId]) {
        filteredObjects[projectId] = {};
      }

      for (const key in payload[projectId]) {
        filteredObjects[projectId][key] = payload[projectId][key];
      }

      localStorage.setItem(storageKey, JSON.stringify(filteredObjects));
    },

    removeFiltersOfProject({ getters }, projectId) {
      const storageKey = getters.getStorageKeyValue;
      let parsedFilteredObjects = JSON.parse(localStorage.getItem(storageKey)) || {};

      if (!(projectId in parsedFilteredObjects)) {
        return;
      }

      const newModifiedFilteredObjects = {};

      for (const key in parsedFilteredObjects) {
        if (key !== projectId) {
          newModifiedFilteredObjects[key] = parsedFilteredObjects[key];
        }
      }

      parsedFilteredObjects = newModifiedFilteredObjects;

      if (Object.keys(parsedFilteredObjects).length === 0) {
        localStorage.removeItem(storageKey);
      } else {
        localStorage.setItem(storageKey, JSON.stringify(parsedFilteredObjects));
      }
    },
  },
};
